/*
 * The product's focus ring, written once.
 *
 * Ported from V1, where it was learned the hard way: the patient link in the
 * queue -- the only keyboard route into a quotation -- suppressed the
 * browser's ring and put no replacement in its place. Tabbing down a
 * twenty-five row queue, focus appeared on the checkbox, VANISHED on the
 * patient name, and reappeared on the next row's checkbox. People pressed
 * Enter without knowing whose row they were on.
 *
 * The cause was that the right ring already existed -- in Button, Checkbox,
 * Tag -- as a literal repeated per ingredient, with the suppressor
 * (`outline-none`) on a SEPARATE line from the replacement. Separated, a new
 * file copies only the suppressor. That happened on nine screens in V1,
 * including login, which had no ring at all.
 *
 * So the pair (suppressor, replacement) is decided HERE, once per use case,
 * and no screen writes the raw class: `focusRing.test.ts` fails any focus
 * literal outside this file.
 *
 * THE TRAP, and the reason half of these constants carry no suppressor: in
 * Tailwind 4 `outline-hidden` sets `--tw-outline-style: none` on the element,
 * and `outline-2` compiles to `outline-style: var(--tw-outline-style)`. Put
 * both on the SAME element and the ring resolves to `outline-style: none` and
 * is simply not drawn -- with the correct `outline-color`, so inspecting it
 * looks right. Measured in V1: with the suppressor, `outlineStyle: "none"`;
 * without it, `solid 2px rgb(77,100,217)` (= `--color-stroke-selected`).
 *
 * Where a suppressor genuinely is needed it is `outline-hidden`, never
 * `outline-none`: `outline-hidden` restores `outline: 2px solid transparent`
 * under `@media (forced-colors: active)`, which is what keeps focus visible in
 * Windows high contrast.
 *
 * The colour is ALWAYS the `stroke-selected` token, never a raw value.
 */

/**
 * Ring OUTSIDE the control (offset +2px). The default for buttons,
 * checkboxes, tags and fields with room around them.
 *
 * Carries no suppressor, which is the opposite of what looks right -- see THE
 * TRAP above. `focus-visible:outline-2` already replaces the browser ring: it
 * sets width, colour and style. Adding `outline-hidden` here ERASES it.
 */
export const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stroke-selected'

/**
 * Ring INSIDE (offset -2px). For a control that meets the edge of its
 * container -- a menu item, a list row -- where a positive offset would spill
 * out and be clipped by the parent's `overflow-hidden`.
 */
export const FOCUS_RING_INSET =
  'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-stroke-selected'

/**
 * The ring for a table row's stretched link (`before:absolute before:inset-0`
 * anchored on the `<tr>`).
 *
 * The focusable element is a truncated `<a>` inside a cell with `max-w-0
 * overflow-hidden`: a ring on the link itself would fence only the visible
 * scrap of text and still be clipped. What a person needs to see is the ROW --
 * exactly the area the click opens -- so the ring goes on the same stretched
 * pseudo-element that already covers it, and is inset because `::before` meets
 * all four edges.
 */
export const FOCUS_RING_STRETCHED_ROW =
  'outline-hidden focus-visible:before:outline-2 focus-visible:before:outline-offset-[-2px] focus-visible:before:outline-stroke-selected'

/**
 * A field whose focus signal is its own border changing colour. The design's
 * treatment for large text boxes, where an outer ring would fight the border
 * that is already there. Still a real replacement: the border moves, and
 * Windows high contrast gets its outline from `outline-hidden`.
 */
export const FOCUS_BORDER = 'outline-hidden focus-visible:border-stroke-selected'

/**
 * The ONLY case where this module hands out a suppressor with no replacement
 * beside it: the inner control of a composite (the `<input>` inside `Input`,
 * the `<select>` inside `Select`) whose wrapper already paints the signal with
 * `focus-within:border-stroke-selected`. A ring on the inner control would
 * draw TWO signals in one box.
 *
 * Use only where the wrapper really carries `focus-within:` -- which is the
 * part no static test can prove for you.
 */
export const FOCUS_OUTLINE_ON_WRAPPER = 'outline-hidden'

/**
 * A row the keyboard pass lands on, whether by Tab or by the screen moving
 * focus there itself.
 *
 * `focus:` rather than `focus-visible:`: the pass focuses the next row after
 * an answer, and a browser may not count that as keyboard-driven, so the
 * `-visible` ring would stay dark on exactly the row somebody has to find.
 * A wash as well as the inset ring, because a two-pixel line on a wide row is
 * easy to lose against the pin bar beside it.
 */
export const FOCUS_RING_ROW =
  'focus:outline-2 focus:outline-offset-[-2px] focus:outline-stroke-selected focus:bg-fill-brand-weak/25'
